import type { ReactNode } from 'react';
import type { Formatter } from '@paige/format';
import type { AccountSummary, CreditFlow } from './account-types';
import { dayWord, runwayTone } from './account-words';
import './insights.css';
import './account.css';

/** The flow's lines that are not zero, in the order the money moves: what the balance is made of. */
function flowLines(flow: CreditFlow): [string, number][] {
  const lines: [string, number][] = [
    ['Left from the period before', flow.opening],
    ['Refill to the plan', flow.refill],
    ['Page packs', flow.packs],
    ['Staff changes', flow.staff],
    ['Used', flow.used],
    ['Held for work in flight', flow.held],
    ['Other', flow.other],
  ];
  return lines.filter(([, v]) => v !== 0);
}

/**
 * How the balance adds up (round 2, every critic's N1): the period's credits by source, summed to
 * the balance; a non-zero drift means the balance cache and the ledger disagree, a ledger fault.
 */
export function CreditArithmetic({ flow, f }: { flow: CreditFlow; f: Formatter }): ReactNode {
  const signed = (n: number) => `${n < 0 ? '−' : '+'}${f.int(Math.abs(Math.round(n)))}`;
  return (
    <details className="ad-flow">
      <summary>How {f.int(Math.round(flow.balance))} adds up</summary>
      <dl>
        {flowLines(flow).map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{signed(v)}</dd>
          </div>
        ))}
        <div className="ad-flow__total">
          <dt>Balance since {dayWord(flow.periodStart)}</dt>
          <dd>{f.int(Math.round(flow.balance))}</dd>
        </div>
      </dl>
      {flow.drift !== 0 ? (
        <p className="ad-flow__fault" role="alert">
          <span aria-hidden="true">⚠</span> The balance and the ledger differ by{' '}
          {signed(flow.drift)}: a ledger fault for engineering.
        </p>
      ) : null}
    </details>
  );
}

/** When the credits run out, said once as a date, with the arithmetic under it. */
export function RunwayTile(x: {
  a: AccountSummary;
  flow?: CreditFlow | undefined;
  f: Formatter;
}): ReactNode {
  const { a, f } = x;
  const c = a.credits;
  const tone = runwayTone(c.held ? 0 : c.runwayDays);
  const value = c.held ? 'held now' : c.runsOutOn ? dayWord(c.runsOutOn) : 'not burning';
  const refill = a.plan.nextRefillOn ? `refill ${dayWord(a.plan.nextRefillOn)}` : 'no refill';
  const gap =
    c.heldDays > 0 ? `; held ${c.heldDays} day${c.heldDays === 1 ? '' : 's'} before it` : '';
  return (
    <div className={`ad-hero__tile ad-hero__tile--${tone}`}>
      <a href="#ad-ledger" className="ad-hero__link">
        <span className="ad-kpi__label">Pages run out</span>
        <span className="ad-hero__value">
          {tone !== 'neutral' ? <span aria-hidden="true">⚠ </span> : null}
          {value}
        </span>
        <span className="ad-kpi__foot">
          {f.int(Math.round(c.balance))} left at {f.int(Math.round(c.burnPerDay))} a day; {refill}
          {gap}
        </span>
      </a>
      {x.flow ? <CreditArithmetic flow={x.flow} f={f} /> : null}
    </div>
  );
}
